/**
 * Single source of truth for the app's color themes, consumed two ways:
 *   - Tailwind classes read the `--c-*` CSS custom properties that
 *     `applyThemeVars()` writes onto <html> (channels, so `/ <alpha-value>`
 *     opacity modifiers work).
 *   - The MUI theme reads `buildMuiPalette()`, which returns real color
 *     strings because MUI runs `alpha()` in JS and cannot resolve CSS var().
 */

import {
  MSI_CLEAN_THEME_ID,
  MSI_CLEAN_THEME_LABEL,
  MSI_COMPONENT_COLORS,
  MSI_PALETTE,
  buildAppStyleTokens,
} from './msiStyleTokens';

export const DEFAULT_DARK_THEME = 'dark';
export const DEFAULT_LIGHT_THEME = 'light';

export const GRADIENT_ICE = 'linear-gradient(90deg, #c8d2db, #8a9aa9)';

// Cold-terminal categorical palette for the attribute scatter plot — blue/teal/
// violet only, no warm reds/oranges/yellows per brief. See buildScatterPalette
// for the 60%-opacity variants appended after these 10.
export const SECTOR_PALETTE = [
  '#4fc3f7',
  '#4db6ac',
  '#7986cb',
  '#9575cd',
  '#26c6da',
  '#4dd0e1',
  '#80cbc4',
  '#64b5f6',
  '#82b1ff',
  '#78909c',
];

// Merges a sparse override map ({ [index]: '#hex' }) over the default palette.
// Bracket access works whether the keys are numbers or (post-JSON) strings.
export function resolveSectorPalette(overrideMap) {
  return SECTOR_PALETTE.map((color, index) => (overrideMap && overrideMap[index]) || color);
}

// Expands to the 20-entry array the scatter plot uses: base hues, then the
// same hues again with 60%-opacity ("99" hex alpha) appended.
export function buildScatterPalette(basePalette = SECTOR_PALETTE) {
  return Array.from({ length: basePalette.length * 2 }, (_, index) => {
    const baseColor = basePalette[index % basePalette.length];
    const isLightVariant = index >= basePalette.length;
    return isLightVariant ? `${baseColor}99` : baseColor;
  });
}

function hexToRgb(hex) {
  const raw = String(hex).replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const int = parseInt(full, 16);
  // eslint-disable-next-line no-bitwise
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgbToHex(r, g, b) {
  const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl(r, g, b) {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l: l * 100 };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rN) {
    h = ((gN - bN) / d) + (gN < bN ? 6 : 0);
  } else if (max === gN) {
    h = ((bN - rN) / d) + 2;
  } else {
    h = ((rN - gN) / d) + 4;
  }
  return { h: h * 60, s: s * 100, l: l * 100 };
}

function hslToRgb(h, s, l) {
  const hN = ((h % 360) + 360) % 360 / 360;
  const sN = s / 100;
  const lN = l / 100;
  if (sN === 0) {
    return { r: lN * 255, g: lN * 255, b: lN * 255 };
  }
  const hue2rgb = (p, q, t) => {
    let tN = t;
    if (tN < 0) tN += 1;
    if (tN > 1) tN -= 1;
    if (tN < 1 / 6) return p + ((q - p) * 6 * tN);
    if (tN < 1 / 2) return q;
    if (tN < 2 / 3) return p + ((q - p) * ((2 / 3) - tN) * 6);
    return p;
  };
  const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - (lN * sN);
  const p = (2 * lN) - q;
  return {
    r: hue2rgb(p, q, hN + (1 / 3)) * 255,
    g: hue2rgb(p, q, hN) * 255,
    b: hue2rgb(p, q, hN - (1 / 3)) * 255,
  };
}

function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

function hslToHex(h, s, l) {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

// Re-hues a color to a fixed saturation/lightness target, keeping only its
// hue. Used to derive the default HC accent/status colors below so they stay
// legible against the pure black/white HC backgrounds. User overrides are
// applied as-is (see applyColorOverrides) — no forced re-derivation, so any
// shade the user picks sticks.
export function deriveContrastColor(hex, { saturation, lightness }) {
  const { h } = hexToHsl(hex);
  return hslToHex(h, saturation, lightness);
}

// Dark HC wants vivid, high-saturation colors that pop on jet black.
// Light HC wants the opposite: duller, darker colors that stay legible on white.
const HC_DARK_SATURATION = 100;
const HC_LIGHT_SATURATION = 45;
const HC_DARK_LIGHTNESS = 70;
const HC_LIGHT_LIGHTNESS = 25;
const HC_DARK_ACCENT_TARGET = { saturation: HC_DARK_SATURATION, lightness: HC_DARK_LIGHTNESS };
const HC_LIGHT_ACCENT_TARGET = { saturation: HC_LIGHT_SATURATION, lightness: HC_LIGHT_LIGHTNESS };

// Every non-black/white "active" color, re-derived at the fixed HC target
// above to seed THEMES below.
const HC_DERIVED_TOKENS = ['accent', 'negative', 'positive', 'optimized', 'ai', 'actual'];

export const THEMES = {
  dark: {
    id: 'dark',
    label: 'Dark',
    mode: 'dark',
    colors: {
      bgBase: '#020409',
      surface: '#070b11',
      elevated: '#0f172a',
      deep: '#020617',
      accent: '#42a5f5',
      border: '#1e293b',
      textPrimary: '#ffffff',
      textSecondary: '#bdbdbd',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  midnight: {
    id: 'midnight',
    label: 'Midnight',
    mode: 'dark',
    colors: {
      bgBase: '#081026',
      surface: '#0f1d3d',
      elevated: '#172a52',
      deep: '#040a1a',
      accent: '#3b82f6',
      border: '#28406b',
      textPrimary: '#ffffff',
      textSecondary: '#a9bbd6',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  carbon: {
    id: 'carbon',
    label: 'Carbon',
    mode: 'dark',
    colors: {
      bgBase: '#1a1c20',
      surface: '#26292f',
      elevated: '#31353c',
      deep: '#121316',
      accent: '#4a93b8',
      border: '#3f444c',
      textPrimary: '#f4f5f7',
      textSecondary: '#a3a9b2',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  // Darkest possible background, lightest possible "active" elements, per
  // accessibility-first IDE high-contrast themes. Table striping is swapped
  // for divider lines on this theme — see tableStyles.js.
  highContrastDark: {
    id: 'highContrastDark',
    label: 'Dark High Contrast',
    mode: 'dark',
    contrast: 'high',
    colors: {
      bgBase: '#000000',
      surface: '#000000',
      elevated: '#000000',
      deep: '#000000',
      // accent/negative/positive/optimized/ai/actual are overwritten below via deriveContrastColor
      accent: '#7ec8ff',
      border: '#ffffff',
      textPrimary: '#ffffff',
      textSecondary: '#d0d0d0',
      negative: '#ff5c5c',
      positive: '#5be787',
      optimized: '#c8f7d4',
      ai: '#a9d6ff',
      actual: '#8ecbff',
    },
  },
  light: {
    id: 'light',
    label: 'Light',
    mode: 'light',
    colors: {
      bgBase: '#eeeeee',
      surface: '#ffffff',
      elevated: '#f8fafc',
      deep: '#e2e8f0',
      accent: '#1565c0',
      border: '#d1d5db',
      textPrimary: '#000000',
      textSecondary: '#424242',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  [MSI_CLEAN_THEME_ID]: {
    id: MSI_CLEAN_THEME_ID,
    label: MSI_CLEAN_THEME_LABEL,
    mode: 'light',
    colors: {
      bgBase: MSI_PALETTE.canvas,
      surface: MSI_PALETTE.surface,
      elevated: MSI_PALETTE.surfaceSubtle,
      deep: MSI_COMPONENT_COLORS.tableHeader,
      accent: MSI_PALETTE.blue600,
      border: MSI_PALETTE.line,
      textPrimary: MSI_PALETTE.ink900,
      textSecondary: MSI_PALETTE.ink500,
      negative: MSI_PALETTE.danger,
      positive: MSI_PALETTE.success,
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: MSI_PALETTE.blue600,
    },
  },
  frost: {
    id: 'frost',
    label: 'Frost',
    mode: 'light',
    colors: {
      bgBase: '#d6e4f0',
      surface: '#eef5fc',
      elevated: '#e1edf8',
      deep: '#c2d6e8',
      accent: '#0b6bcb',
      border: '#a5c4e0',
      textPrimary: '#0b2540',
      textSecondary: '#3d5a78',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  slate: {
    id: 'slate',
    label: 'Slate',
    mode: 'light',
    colors: {
      bgBase: '#c9ced6',
      surface: '#dfe3e9',
      elevated: '#d4d9e0',
      deep: '#b7bdc7',
      accent: '#37474f',
      border: '#9aa3af',
      textPrimary: '#1a2129',
      textSecondary: '#48515c',
      negative: '#c62828',
      positive: '#2e7d32',
      optimized: '#a5d6a7',
      ai: '#90caf9',
      actual: '#42a5f5',
    },
  },
  // Inverse of highContrastDark: lightest possible background with the
  // darkest possible "active" elements.
  highContrastLight: {
    id: 'highContrastLight',
    label: 'Light High Contrast',
    mode: 'light',
    contrast: 'high',
    colors: {
      bgBase: '#ffffff',
      surface: '#ffffff',
      elevated: '#ffffff',
      deep: '#ffffff',
      // accent/negative/positive/optimized/ai/actual are overwritten below via deriveContrastColor
      accent: '#0a3d7a',
      border: '#000000',
      textPrimary: '#000000',
      textSecondary: '#2b2b2b',
      negative: '#a30000',
      positive: '#0b6b1f',
      optimized: '#0f4d22',
      ai: '#123a7a',
      actual: '#123a7a',
    },
  },
};

// Overwrite the HC placeholder hexes above by re-deriving each from its
// base (non-HC) hue.
HC_DERIVED_TOKENS.forEach((token) => {
  THEMES.highContrastDark.colors[token] = deriveContrastColor(THEMES.dark.colors[token], HC_DARK_ACCENT_TARGET);
  THEMES.highContrastLight.colors[token] = deriveContrastColor(THEMES.light.colors[token], HC_LIGHT_ACCENT_TARGET);
});

// Token name -> CSS custom property. Keep in sync with tailwind.config.js.
const CSS_VAR_BY_TOKEN = {
  bgBase: '--c-bg',
  surface: '--c-surface',
  elevated: '--c-elevated',
  deep: '--c-deep',
  accent: '--c-accent',
  border: '--c-border',
  textPrimary: '--c-text',
  textSecondary: '--c-text-secondary',
  negative: '--c-negative',
  positive: '--c-positive',
  optimized: '--c-optimized',
  ai: '--c-ai',
  actual: '--c-actual',
};

// "#1565c0" -> "21 101 192", the channel form needed by rgb(... / <alpha>).
function hexToRgbChannels(hex) {
  const raw = String(hex).replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const int = parseInt(full, 16);
  // eslint-disable-next-line no-bitwise
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
}

// Semantic tokens a user can recolor from the Appearance tab. The attribute
// palette is excluded — it's a categorical array, not a single swatch.
export const OVERRIDABLE_TOKENS = [
  { token: 'optimized', cssVar: '--c-optimized', label: 'Optimized', description: 'Highlight for optimizer-suggested weights and figures.' },
  { token: 'actual', cssVar: '--c-actual', label: 'Actual', description: 'Bar color for actual (non-optimized) portfolio values in attribute/factor charts.' },
  { token: 'ai', cssVar: '--c-ai', label: 'AI', description: 'Accent for AI-derived idio-vol values and badges.' },
  { token: 'positive', cssVar: '--c-positive', label: 'Positive', description: 'Gains and other positive deltas.' },
  { token: 'negative', cssVar: '--c-negative', label: 'Negative', description: 'Losses and other negative deltas.' },
];

export function resolveThemeId(id) {
  return THEMES[id] ? id : DEFAULT_DARK_THEME;
}

// Picker-facing metadata for every theme, dark group first then light. The
// picker renders straight from this, so adding a theme above wires it in here.
export const THEME_LIST = ['dark', 'midnight', 'carbon', 'highContrastDark', 'light', MSI_CLEAN_THEME_ID, 'frost', 'slate', 'highContrastLight'].map((id) => {
  const { label, mode } = THEMES[id];
  return { id, label, mode, colors: THEMES[id].colors };
});

export function isThemeDark(id) {
  return THEMES[resolveThemeId(id)].mode === 'dark';
}

export function isThemeHighContrast(id) {
  return THEMES[resolveThemeId(id)].contrast === 'high';
}

// Fixed brand hue for the admin status indicator (AppBar pill + profile-menu
// outline) -- deliberately not one of OVERRIDABLE_TOKENS, since Trevor asked
// for consistency across users/themes rather than something a user can
// recolor. Same amber hue everywhere; lightness/saturation flips by
// light-vs-dark mode (bright gold on dark backgrounds, deep amber on light
// ones) so it stays legible without ever changing color family. HC themes
// reuse the existing HC saturation/lightness targets for the same reason
// deriveContrastColor does, so the pill matches the app's other HC-derived
// accents.
const ADMIN_ACCENT_HUE = 45;
export function getAdminAccentColor(themeId) {
  const theme = THEMES[resolveThemeId(themeId)];
  const isDark = theme.mode === 'dark';
  if (theme.contrast === 'high') {
    return isDark
      ? hslToHex(ADMIN_ACCENT_HUE, HC_DARK_SATURATION, HC_DARK_LIGHTNESS)
      : hslToHex(ADMIN_ACCENT_HUE, HC_LIGHT_SATURATION, HC_LIGHT_LIGHTNESS);
  }
  return isDark
    ? hslToHex(ADMIN_ACCENT_HUE, 100, 62)
    : hslToHex(ADMIN_ACCENT_HUE, 88, 32);
}

// The theme's built-in hex for a token, i.e. the value shown when no override
// is set and the target a reset returns to.
export function getThemeColor(themeId, token) {
  return THEMES[resolveThemeId(themeId)].colors[token];
}

// Run from a layout effect so the variables land before the browser paints.
export function applyThemeVars(themeId, root = (typeof document !== 'undefined' ? document.documentElement : null)) {
  if (!root) {
    return;
  }
  const theme = THEMES[resolveThemeId(themeId)];
  Object.entries(CSS_VAR_BY_TOKEN).forEach(([token, varName]) => {
    root.style.setProperty(varName, hexToRgbChannels(theme.colors[token]));
  });
  const appStyle = buildAppStyleTokens(theme);
  Object.entries(appStyle.palette).forEach(([role, value]) => {
    root.style.setProperty(`--app-${role}`, value);
  });
  Object.entries(appStyle.componentColors).forEach(([role, value]) => {
    if (role === 'semanticBorders') {
      Object.entries(value).forEach(([toneName, toneValue]) => {
        root.style.setProperty(`--app-semantic-border-${toneName}`, toneValue);
      });
      return;
    }
    root.style.setProperty(`--app-${role}`, value);
  });
  root.style.setProperty('--gradient-ice', GRADIENT_ICE);
  root.classList.toggle('dark', theme.mode === 'dark');
  root.setAttribute('data-theme', theme.id);
}

// Layers per-user color overrides on top of the active theme. Call after
// applyThemeVars, which resets every var to the theme default first.
export function applyColorOverrides(overrides, root = (typeof document !== 'undefined' ? document.documentElement : null)) {
  if (!root || !overrides) {
    return;
  }
  OVERRIDABLE_TOKENS.forEach(({ token, cssVar }) => {
    const hex = overrides[token];
    if (hex) {
      root.style.setProperty(cssVar, hexToRgbChannels(hex));
    }
  });

  const activeThemeId = root.getAttribute?.('data-theme');
  const activeTheme = THEMES[resolveThemeId(activeThemeId)];
  const mergedColors = { ...activeTheme.colors, ...overrides };
  const appStyle = buildAppStyleTokens({ ...activeTheme, colors: mergedColors });
  if (overrides.positive) {
    // The financial --c-* hue remains literal; derived status roles may tune
    // its foreground against the generated fill to preserve small-text contrast.
    root.style.setProperty('--app-success', appStyle.palette.success);
    root.style.setProperty('--app-successBg', appStyle.palette.successBg);
    root.style.setProperty('--app-semantic-border-success', appStyle.componentColors.semanticBorders.success);
  }
  if (overrides.negative) {
    root.style.setProperty('--app-danger', appStyle.palette.danger);
    root.style.setProperty('--app-dangerBg', appStyle.palette.dangerBg);
    root.style.setProperty('--app-semantic-border-danger', appStyle.componentColors.semanticBorders.danger);
  }
}

export function buildMuiPalette(themeId) {
  const theme = THEMES[resolveThemeId(themeId)];
  const { mode, colors } = theme;
  const appStyle = buildAppStyleTokens(theme);
  return {
    mode,
    primary: { main: colors.accent },
    divider: colors.border,
    background: { default: colors.bgBase, paper: colors.surface },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary },
    // Consumed by table helpers (see tableStyles.js) to preserve flat rows
    // while giving body-row separators a quieter high-contrast treatment.
    highContrast: theme.contrast === 'high',
    // One universal MSI-derived component grammar consumes this complete,
    // palette-specific role contract for every registered appearance theme.
    appStyle,
  };
}

export function getThemeAppStyleTokens(themeId) {
  return buildAppStyleTokens(THEMES[resolveThemeId(themeId)]);
}
